import { useState, useMemo } from 'react'
import { Search, ChevronDown, ChevronRight, Copy, Check, Lock, Book, Code, Key, Zap, Shield, Globe, Mail, Users, FileText, Megaphone, Inbox, Bot, BarChart3, Settings, Webhook, AlertTriangle } from 'lucide-react'
import './ApiDocs.css'

const API_SECTIONS = [
  {
    id: 'campaigns',
    title: 'Campaigns',
    icon: Megaphone,
    scope: 'campaigns',
    description: 'Create, manage, and control email campaigns with sequences, schedules, and leads',
    endpoints: [
      {
        method: 'GET',
        path: '/api/v1/campaigns',
        summary: 'List all campaigns',
        response: `{
  "campaigns": [
    {
      "id": 1,
      "name": "Q1 Outreach",
      "status": "active",
      "pause_reason": null,
      "started_at": "2026-01-16T09:00:00",
      "created_at": "2026-01-15T10:00:00"
    }
  ]
}`
      },
      {
        method: 'POST',
        path: '/api/v1/campaigns',
        summary: 'Create a new campaign',
        body: [
          { name: 'name', type: 'string', required: true, desc: 'Campaign name' },
          { name: 'sender_ids', type: 'integer[]', required: false, desc: 'Sender account IDs (can be empty)' }
        ],
        example: `curl -X POST https://api.synxreach.com/api/v1/campaigns \\
  -H "X-API-Key: sx_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Spring Campaign 2026", "sender_ids": []}'`,
        response: `{
  "message": "Campaign created successfully",
  "id": 42
}`
      },
      {
        method: 'GET',
        path: '/api/v1/campaigns/{campaign_id}',
        summary: 'Get campaign details',
        response: `{
  "id": 42,
  "name": "Spring Campaign 2026",
  "description": "Spring Campaign 2026",
  "status": "draft",
  "pause_reason": null,
  "started_at": null,
  "paused_at": null,
  "completed_at": null,
  "created_at": "2026-03-15T10:00:00"
}`
      },
      {
        method: 'PUT',
        path: '/api/v1/campaigns/{campaign_id}',
        summary: 'Update a campaign',
        body: [
          { name: 'name', type: 'string', required: true, desc: 'Updated campaign name' },
          { name: 'sender_ids', type: 'integer[]', required: false, desc: 'Sender account IDs' }
        ]
      },
      {
        method: 'DELETE',
        path: '/api/v1/campaigns/{campaign_id}',
        summary: 'Delete a campaign'
      },
      {
        method: 'POST',
        path: '/api/v1/campaigns/{campaign_id}/start',
        summary: 'Start a campaign',
        description: 'Activates the campaign and begins sending emails. Requires leads, sequence, schedule, and email accounts to be configured first.',
        response: `{
  "message": "Campaign started successfully",
  "campaign_id": 42,
  "status": "active"
}`
      },
      {
        method: 'POST',
        path: '/api/v1/campaigns/{campaign_id}/pause',
        summary: 'Pause a campaign'
      },
      {
        method: 'POST',
        path: '/api/v1/campaigns/{campaign_id}/resume',
        summary: 'Resume a paused campaign'
      },
      {
        method: 'POST',
        path: '/api/v1/campaigns/{campaign_id}/complete',
        summary: 'Mark campaign as complete'
      },
      {
        method: 'POST',
        path: '/api/v1/campaigns/{campaign_id}/sequence',
        summary: 'Create/update email sequence',
        description: 'Define the multi-step email sequence. Each step has a delay (in days) and one or more variants with subject and body content. No template ID needed — send content directly.',
        body: [
          { name: 'steps', type: 'array', required: true, desc: 'Array of step objects' },
          { name: 'steps[].delay', type: 'integer', required: true, desc: 'Delay in days before sending this step (0 for immediate)' },
          { name: 'steps[].variants', type: 'array', required: true, desc: 'Array of variant objects' },
          { name: 'steps[].variants[].id', type: 'string', required: true, desc: 'Variant label — "A" or "B"' },
          { name: 'steps[].variants[].subject', type: 'string', required: true, desc: 'Email subject line' },
          { name: 'steps[].variants[].body', type: 'string', required: true, desc: 'Email body (HTML)' },
          { name: 'steps[].variants[].split', type: 'integer', required: false, desc: 'Split percentage (default: 50)' }
        ],
        example: `curl -X POST https://api.synxreach.com/api/v1/campaigns/42/sequence \\
  -H "X-API-Key: sx_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "steps": [
      {
        "delay": 0,
        "variants": [
          {
            "id": "A",
            "subject": "Quick question about {{company}}",
            "body": "<p>Hi {{name}}, I noticed your work at {{company}}...</p>",
            "split": 100
          }
        ]
      },
      {
        "delay": 3,
        "variants": [
          {
            "id": "A",
            "subject": "Following up",
            "body": "<p>Hi {{name}}, just wanted to follow up on my last email...</p>",
            "split": 100
          }
        ]
      }
    ]
  }'`,
        notes: 'You always send subject and body content directly. The system creates templates internally — no need to pass master_template_id.'
      },
      {
        method: 'GET',
        path: '/api/v1/campaigns/{campaign_id}/sequence',
        summary: 'Get campaign sequence',
        response: `{
  "campaign_id": 42,
  "steps": [
    {
      "step_number": 1,
      "delay_days": 0,
      "variants": [
        {
          "id": "A",
          "subject": "Quick question about {{company}}",
          "body": "<p>Hi {{name}}...</p>",
          "split": 100
        }
      ]
    }
  ]
}`
      },
      {
        method: 'POST',
        path: '/api/v1/campaigns/{campaign_id}/schedule',
        summary: 'Set campaign schedule',
        body: [
          { name: 'schedule_name', type: 'string', required: true, desc: 'Schedule name' },
          { name: 'start_date', type: 'date', required: false, desc: 'Start date (YYYY-MM-DD)' },
          { name: 'end_date', type: 'date', required: false, desc: 'End date (YYYY-MM-DD)' },
          { name: 'from_time', type: 'time', required: false, desc: 'Send window start (HH:MM)' },
          { name: 'to_time', type: 'time', required: false, desc: 'Send window end (HH:MM)' },
          { name: 'timezone', type: 'string', required: true, desc: 'Timezone (e.g. America/New_York)' },
          { name: 'days', type: 'string[]', required: true, desc: 'Days to send (e.g. ["Monday","Tuesday"])' }
        ],
        example: `curl -X POST https://api.synxreach.com/api/v1/campaigns/42/schedule \\
  -H "X-API-Key: sx_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "schedule_name": "Weekday mornings",
    "start_date": "2026-03-20",
    "end_date": "2026-04-30",
    "from_time": "09:00",
    "to_time": "12:00",
    "timezone": "America/New_York",
    "days": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
  }'`
      },
      {
        method: 'GET',
        path: '/api/v1/campaigns/{campaign_id}/schedule',
        summary: 'Get campaign schedule'
      },
      {
        method: 'POST',
        path: '/api/v1/campaigns/{campaign_id}/leads',
        summary: 'Upload leads via CSV/Excel',
        body: [
          { name: 'file', type: 'file', required: true, desc: 'CSV or Excel file with at least an "email" column' }
        ],
        example: `curl -X POST https://api.synxreach.com/api/v1/campaigns/42/leads \\
  -H "X-API-Key: sx_live_your_key_here" \\
  -F "file=@contacts.csv"`,
        response: `{
  "message": "Successfully processed 150 contacts",
  "count": 150
}`
      },
      {
        method: 'POST',
        path: '/api/v1/campaigns/{campaign_id}/options',
        summary: 'Set campaign options',
        body: [
          { name: 'account_ids', type: 'integer[]', required: true, desc: 'Email account IDs to send from' },
          { name: 'emails_per_day', type: 'integer', required: false, desc: 'Max emails per day (default: 50)' },
          { name: 'stop_on_reply', type: 'boolean', required: false, desc: 'Stop sending to contacts who reply (default: true)' }
        ],
        example: `curl -X POST https://api.synxreach.com/api/v1/campaigns/42/options \\
  -H "X-API-Key: sx_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "account_ids": [1, 2],
    "emails_per_day": 100,
    "stop_on_reply": true
  }'`
      },
      {
        method: 'GET',
        path: '/api/v1/campaigns/email-accounts',
        summary: 'List email accounts available for campaigns',
        response: `{
  "accounts": [
    {
      "id": 1,
      "email": "john@company.com",
      "first_name": "John",
      "last_name": "Doe",
      "is_verified": true
    }
  ]
}`
      },
      {
        method: 'GET',
        path: '/api/v1/campaigns/{campaign_id}/analytics',
        summary: 'Campaign analytics summary',
        response: `{
  "campaign_id": 42,
  "campaign_name": "Spring Campaign 2026",
  "total_sent": 500,
  "total_delivered": 495,
  "total_bounced": 5,
  "unique_opens": 215,
  "unique_clicks": 48,
  "delivery_rate": 99.0,
  "open_rate": 43.43,
  "click_rate": 9.7,
  "bounce_rate": 1.0
}`
      }
    ]
  },
  {
    id: 'templates',
    title: 'Templates',
    icon: FileText,
    scope: 'templates',
    description: 'Fetch your master email templates',
    endpoints: [
      {
        method: 'GET',
        path: '/api/v1/templates',
        summary: 'List all templates',
        example: `curl "https://api.synxreach.com/api/v1/templates" \\
  -H "X-API-Key: sx_live_your_key_here"`,
        response: `{
  "templates": [
    {
      "id": 1,
      "name": "Welcome Series",
      "subject": "Welcome to {{company}}",
      "created_at": "2026-01-15T10:00:00"
    }
  ]
}`
      },
      {
        method: 'GET',
        path: '/api/v1/templates/{template_id}',
        summary: 'Get template by ID',
        response: `{
  "id": 1,
  "name": "Welcome Series",
  "subject": "Welcome to {{company}}",
  "html": "<p>Welcome aboard!</p>",
  "created_at": "2026-01-15T10:00:00"
}`
      }
    ]
  },
  {
    id: 'reports',
    title: 'Reports & Analytics',
    icon: BarChart3,
    scope: 'analytics',
    description: 'Access comprehensive reports, dashboards, and export data',
    endpoints: [
      {
        method: 'GET',
        path: '/api/v1/reports/dashboard',
        summary: 'Dashboard summary (KPIs, trends, best/worst)',
        example: `curl "https://api.synxreach.com/api/v1/reports/dashboard" \\
  -H "X-API-Key: sx_live_your_key_here"`,
        response: `{
  "kpis": {
    "total_sent": 5000,
    "total_delivered": 4950,
    "total_opens": 2150,
    "total_clicks": 480,
    "total_bounced": 50,
    "total_replies": 120,
    "avg_open_rate": 43.43,
    "avg_click_rate": 9.7,
    "avg_bounce_rate": 1.0
  },
  "status_breakdown": {"active": 3, "paused": 1, "completed": 5},
  "daily_trend": [
    {"date": "2026-03-14", "sent": 200, "opens": 85, "clicks": 18}
  ],
  "best_campaign": {"id": 1, "name": "Q1 Outreach", "open_rate": 52.3},
  "worst_campaign": {"id": 3, "name": "Newsletter", "open_rate": 18.5}
}`
      },
      {
        method: 'GET',
        path: '/api/v1/reports/campaigns/{campaign_id}',
        summary: 'Detailed campaign report',
        response: `{
  "campaign_id": 42,
  "campaign_name": "Spring Campaign 2026",
  "status": "active",
  "total_contacts": 500,
  "replies": 23,
  "reply_rate": 4.65,
  "sent": 495,
  "delivered": 490,
  "bounced": 5,
  "unique_opens": 215,
  "unique_clicks": 48,
  "open_rate": 43.88,
  "click_rate": 9.8
}`
      },
      {
        method: 'GET',
        path: '/api/v1/reports/campaign-comparison',
        summary: 'Compare 2+ campaigns side by side',
        params: [
          { name: 'campaign_ids', type: 'string', desc: 'Comma-separated campaign IDs (e.g. 1,2,3)' }
        ],
        example: `curl "https://api.synxreach.com/api/v1/reports/campaign-comparison?campaign_ids=1,2,3" \\
  -H "X-API-Key: sx_live_your_key_here"`
      },
      {
        method: 'GET',
        path: '/api/v1/reports/export/{report_type}',
        summary: 'Export report as CSV',
        description: 'Supported report types: campaign_stats. Returns a CSV file download.',
        example: `curl "https://api.synxreach.com/api/v1/reports/export/campaign_stats" \\
  -H "X-API-Key: sx_live_your_key_here" \\
  -o report.csv`
      }
    ]
  },
  {
    id: 'webhooks',
    title: 'Webhooks',
    icon: Webhook,
    scope: 'webhooks',
    description: 'Register your endpoint URL and SynxReach will automatically POST event data to it whenever emails are sent, opened, replied to, bounced, or when campaign status changes',
    endpoints: [
      {
        method: 'POST',
        path: '/api/v1/settings/webhooks',
        summary: 'Create a webhook',
        body: [
          { name: 'url', type: 'string', required: true, desc: 'Webhook endpoint URL (HTTPS required, HTTP allowed for localhost)' },
          { name: 'events', type: 'string[]', required: true, desc: 'Events to subscribe: send, reply, bounce, open, click, status_change, unsubscribe' },
          { name: 'description', type: 'string', required: false, desc: 'Description (max 500 chars)' }
        ],
        example: `curl -X POST https://api.synxreach.com/api/v1/settings/webhooks \\
  -H "X-API-Key: sx_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://your-app.com/webhooks/synxreach",
    "events": ["send", "reply", "bounce", "open"],
    "description": "Production email events"
  }'`,
        response: `{
  "id": 5,
  "url": "https://your-app.com/webhooks/synxreach",
  "events": ["send", "reply", "bounce", "open"],
  "is_active": true,
  "secret": "whsec_a1b2c3d4e5f6...",
  "description": "Production email events",
  "created_at": "2026-03-11T10:30:00+00:00"
}`,
        notes: 'The webhook secret (whsec_*) is only returned at creation time. Store it securely to verify incoming webhook signatures.'
      },
      {
        method: 'GET',
        path: '/api/v1/settings/webhooks',
        summary: 'List all webhooks',
        example: `curl "https://api.synxreach.com/api/v1/settings/webhooks" \\
  -H "X-API-Key: sx_live_your_key_here"`,
        response: `{
  "items": [
    {
      "id": 5,
      "url": "https://your-app.com/webhooks/synxreach",
      "events": ["send", "reply", "bounce", "open"],
      "is_active": true,
      "description": "Production email events",
      "created_at": "2026-03-11T10:30:00+00:00",
      "updated_at": "2026-03-11T10:30:00+00:00"
    }
  ],
  "total": 1
}`
      },
      {
        method: 'PUT',
        path: '/api/v1/settings/webhooks/{webhook_id}',
        summary: 'Update a webhook',
        body: [
          { name: 'url', type: 'string', required: false, desc: 'New URL (HTTPS required)' },
          { name: 'events', type: 'string[]', required: false, desc: 'New event list' },
          { name: 'is_active', type: 'boolean', required: false, desc: 'Enable or disable the webhook' },
          { name: 'description', type: 'string', required: false, desc: 'New description' }
        ],
        example: `curl -X PUT https://api.synxreach.com/api/v1/settings/webhooks/5 \\
  -H "X-API-Key: sx_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "events": ["send", "reply", "bounce", "open", "click"],
    "is_active": true
  }'`
      },
      {
        method: 'DELETE',
        path: '/api/v1/settings/webhooks/{webhook_id}',
        summary: 'Delete a webhook',
        example: `curl -X DELETE https://api.synxreach.com/api/v1/settings/webhooks/5 \\
  -H "X-API-Key: sx_live_your_key_here"`,
        response: `{
  "message": "Webhook deleted successfully",
  "id": 5
}`
      },
      {
        method: 'POST',
        path: '/api/v1/settings/webhooks/{webhook_id}/test',
        summary: 'Send a test event to your webhook',
        description: 'Fires a test delivery to your webhook URL with sample data. Use this to verify your endpoint is receiving and processing events correctly.',
        body: [
          { name: 'event', type: 'string', required: false, desc: 'Event type to simulate (default: "send"). Options: send, reply, bounce, open, click, status_change, unsubscribe' }
        ],
        example: `curl -X POST https://api.synxreach.com/api/v1/settings/webhooks/5/test \\
  -H "X-API-Key: sx_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"event": "send"}'`,
        response: `{
  "success": true,
  "status_code": 200,
  "response_time_ms": 142,
  "response_body": "OK"
}`
      },
      {
        method: 'GET',
        path: '/api/v1/settings/webhooks/{webhook_id}/deliveries',
        summary: 'Get webhook delivery logs',
        description: 'View the history of webhook deliveries including success/failure status. Use this to debug delivery issues.',
        params: [
          { name: 'page', type: 'integer', desc: 'Page number (default: 1)' },
          { name: 'per_page', type: 'integer', desc: 'Items per page (1-100, default: 25)' },
          { name: 'success', type: 'boolean', desc: 'Filter by delivery success (true/false)' }
        ],
        example: `curl "https://api.synxreach.com/api/v1/settings/webhooks/5/deliveries?page=1&per_page=10" \\
  -H "X-API-Key: sx_live_your_key_here"`,
        response: `{
  "items": [
    {
      "id": 101,
      "event_type": "send",
      "success": true,
      "response_status": 200,
      "attempt_number": 1,
      "delivered_at": "2026-03-15T14:22:00+00:00"
    }
  ],
  "total": 42,
  "page": 1,
  "per_page": 10
}`
      }
    ]
  }
]

const SCOPES_INFO = [
  { scope: 'campaigns', desc: 'Create, read, update, delete campaigns, sequences, schedules, leads, and options', color: '#3b82f6' },
  { scope: 'templates', desc: 'List and fetch master email templates', color: '#f59e0b' },
  { scope: 'analytics', desc: 'Access reports, dashboards, and CSV exports', color: '#22c55e' },
  { scope: 'webhooks', desc: 'Create, update, delete, and test webhooks; view delivery logs', color: '#a855f7' }
]

const METHOD_COLORS = {
  GET: { bg: 'rgba(34,197,94,0.12)', color: '#22c55e' },
  POST: { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6' },
  PUT: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  PATCH: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  DELETE: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444' }
}

const API_HOST = 'https://api.synxreach.com'

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button className="apidocs-copy-btn" onClick={handleCopy} title="Copy">
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  )
}

function EndpointCard({ endpoint }) {
  const [isOpen, setIsOpen] = useState(false)
  const mc = METHOD_COLORS[endpoint.method] || METHOD_COLORS.GET

  const fullUrl = `${API_HOST}${endpoint.path}`

  return (
    <div className={`apidocs-endpoint ${isOpen ? 'open' : ''}`}>
      <div className="apidocs-endpoint-header" onClick={() => setIsOpen(!isOpen)}>
        <span className="apidocs-method" style={{ background: mc.bg, color: mc.color }}>
          {endpoint.method}
        </span>
        <span className="apidocs-path">{endpoint.path}</span>
        <CopyButton text={fullUrl} />
        <span className="apidocs-summary">{endpoint.summary}</span>
        {isOpen ? <ChevronDown size={16} className="apidocs-chevron" /> : <ChevronRight size={16} className="apidocs-chevron" />}
      </div>
      {isOpen && (
        <div className="apidocs-endpoint-body">
          {endpoint.description && (
            <p className="apidocs-endpoint-desc">{endpoint.description}</p>
          )}

          {endpoint.params && (
            <>
              <div className="apidocs-label">Query Parameters</div>
              <table className="apidocs-param-table">
                <thead>
                  <tr><th>Parameter</th><th>Type</th><th>Description</th></tr>
                </thead>
                <tbody>
                  {endpoint.params.map(p => (
                    <tr key={p.name}>
                      <td><code>{p.name}</code></td>
                      <td><span className="apidocs-type">{p.type}</span></td>
                      <td>{p.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {endpoint.body && (
            <>
              <div className="apidocs-label">Request Body</div>
              <table className="apidocs-param-table">
                <thead>
                  <tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr>
                </thead>
                <tbody>
                  {endpoint.body.map(p => (
                    <tr key={p.name}>
                      <td><code>{p.name}</code></td>
                      <td><span className="apidocs-type">{p.type}</span></td>
                      <td>
                        {p.required
                          ? <span className="apidocs-required">Required</span>
                          : <span className="apidocs-optional">Optional</span>
                        }
                      </td>
                      <td>{p.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {endpoint.example && (
            <>
              <div className="apidocs-label">Example Request</div>
              <div className="apidocs-code-wrapper">
                <CopyButton text={endpoint.example} />
                <pre><code>{endpoint.example}</code></pre>
              </div>
            </>
          )}

          {endpoint.response && (
            <>
              <div className="apidocs-label">Response <span style={{ color: '#22c55e' }}>200</span></div>
              <div className="apidocs-code-wrapper">
                <CopyButton text={endpoint.response} />
                <pre><code>{endpoint.response}</code></pre>
              </div>
            </>
          )}

          {endpoint.notes && (
            <div className="apidocs-note">
              <AlertTriangle size={14} />
              <span>{endpoint.notes}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ApiDocs() {
  const [search, setSearch] = useState('')
  const [activeSection, setActiveSection] = useState(null)

  const filteredSections = useMemo(() => {
    if (!search) return API_SECTIONS
    const q = search.toLowerCase()
    return API_SECTIONS.map(section => ({
      ...section,
      endpoints: section.endpoints.filter(ep =>
        ep.path.toLowerCase().includes(q) ||
        ep.summary.toLowerCase().includes(q) ||
        ep.method.toLowerCase().includes(q) ||
        section.title.toLowerCase().includes(q)
      )
    })).filter(s => s.endpoints.length > 0)
  }, [search])

  const totalEndpoints = API_SECTIONS.reduce((sum, s) => sum + s.endpoints.length, 0)

  return (
    <div className="page apidocs-page animate-fade-in">
      {/* Header */}
      <div className="apidocs-hero">
        <div className="apidocs-hero-content">
          <div className="apidocs-hero-icon">
            <Book size={28} />
          </div>
          <div>
            <h2>API Documentation</h2>
            <p>Use your API key to access {totalEndpoints} endpoints. Manage campaigns, templates, and reports programmatically.</p>
          </div>
        </div>
        <div className="apidocs-hero-meta">
          <div className="apidocs-base-url">
            <span className="apidocs-base-label">Base URL</span>
            <code>{API_HOST}/api/v1</code>
            <CopyButton text={`${API_HOST}/api/v1`} />
          </div>
        </div>
      </div>

      {/* Authentication */}
      <div className="apidocs-info-card">
        <div className="apidocs-auth-info-header">
          <Key size={18} />
          <strong>Authentication</strong>
        </div>
        <p>Pass your API key in the <code>X-API-Key</code> header with every request. Create API keys from <strong>Settings &rarr; API Keys</strong>.</p>
        <div className="apidocs-code-wrapper" style={{ marginTop: '0.75rem' }}>
          <CopyButton text={'X-API-Key: sx_live_your_key_here'} />
          <pre><code>{'X-API-Key: sx_live_your_key_here'}</code></pre>
        </div>
        <div className="apidocs-key-formats">
          <div className="apidocs-key-format">
            <code className="apidocs-key-live">sx_live_*</code>
            <span>Live environment &mdash; production use</span>
          </div>
          <div className="apidocs-key-format">
            <code className="apidocs-key-test">sx_test_*</code>
            <span>Test environment &mdash; development &amp; testing</span>
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div className="apidocs-info-card">
        <div className="apidocs-auth-info-header">
          <Shield size={18} />
          <strong>Rate Limits</strong>
        </div>
        <p>Each API key has configurable rate limits (default: <code>60/min</code> and <code>10,000/day</code>). Exceeding the limit returns <code>429 Too Many Requests</code>.</p>
      </div>

      {/* Scopes */}
      <div className="apidocs-info-card">
        <div className="apidocs-auth-info-header">
          <Lock size={18} />
          <strong>Scopes</strong>
        </div>
        <p>Your API key must include the required scope to access an endpoint. The public API supports the following scopes:</p>
        <table className="apidocs-param-table" style={{ marginTop: '0.5rem' }}>
          <thead>
            <tr><th>Scope</th><th>Access</th></tr>
          </thead>
          <tbody>
            {SCOPES_INFO.map(s => (
              <tr key={s.scope}>
                <td><code style={{ color: s.color }}>{s.scope}</code></td>
                <td>{s.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Error Handling */}
      <div className="apidocs-info-card">
        <div className="apidocs-auth-info-header">
          <Code size={18} />
          <strong>Errors</strong>
        </div>
        <div className="apidocs-status-grid">
          <div className="apidocs-status-item"><span className="apidocs-status-code success">200</span> Success</div>
          <div className="apidocs-status-item"><span className="apidocs-status-code success">201</span> Created</div>
          <div className="apidocs-status-item"><span className="apidocs-status-code warn">400</span> Bad Request</div>
          <div className="apidocs-status-item"><span className="apidocs-status-code warn">401</span> Invalid API Key</div>
          <div className="apidocs-status-item"><span className="apidocs-status-code warn">403</span> Scope Not Allowed</div>
          <div className="apidocs-status-item"><span className="apidocs-status-code error">404</span> Not Found</div>
          <div className="apidocs-status-item"><span className="apidocs-status-code error">422</span> Validation Error</div>
          <div className="apidocs-status-item"><span className="apidocs-status-code error">429</span> Rate Limited</div>
        </div>
        <div className="apidocs-code-wrapper" style={{ marginTop: '0.75rem' }}>
          <pre><code>{'{"detail": "Invalid or expired API key"}'}</code></pre>
        </div>
      </div>

      {/* Quick Start Guide */}
      <div className="apidocs-info-card">
        <div className="apidocs-auth-info-header">
          <Zap size={18} />
          <strong>Quick Start</strong>
        </div>
        <p>Follow these steps to launch your first campaign via API:</p>
        <div className="apidocs-quickstart-steps">
          <div className="apidocs-quickstart-step">
            <span className="apidocs-step-num">1</span>
            <span>Create a campaign: <code>POST /api/v1/campaigns</code></span>
          </div>
          <div className="apidocs-quickstart-step">
            <span className="apidocs-step-num">2</span>
            <span>Set sequence with email content: <code>POST /api/v1/campaigns/{'{'}<em>id</em>{'}'}/sequence</code></span>
          </div>
          <div className="apidocs-quickstart-step">
            <span className="apidocs-step-num">3</span>
            <span>Configure schedule: <code>POST /api/v1/campaigns/{'{'}<em>id</em>{'}'}/schedule</code></span>
          </div>
          <div className="apidocs-quickstart-step">
            <span className="apidocs-step-num">4</span>
            <span>Upload leads: <code>POST /api/v1/campaigns/{'{'}<em>id</em>{'}'}/leads</code></span>
          </div>
          <div className="apidocs-quickstart-step">
            <span className="apidocs-step-num">5</span>
            <span>Set sending options: <code>POST /api/v1/campaigns/{'{'}<em>id</em>{'}'}/options</code></span>
          </div>
          <div className="apidocs-quickstart-step">
            <span className="apidocs-step-num">6</span>
            <span>Start sending: <code>POST /api/v1/campaigns/{'{'}<em>id</em>{'}'}/start</code></span>
          </div>
        </div>
      </div>

      {/* How Webhooks Work */}
      <div className="apidocs-info-card">
        <div className="apidocs-auth-info-header">
          <Webhook size={18} />
          <strong>How Webhooks Work</strong>
        </div>
        <p style={{ marginBottom: '0.75rem' }}>
          Webhooks are <strong>automatic outbound notifications</strong> from SynxReach to your server. You don't call us — we call you.
        </p>
        <div className="apidocs-quickstart-steps">
          <div className="apidocs-quickstart-step">
            <span className="apidocs-step-num">1</span>
            <span>Register your endpoint URL and choose which events to subscribe to</span>
          </div>
          <div className="apidocs-quickstart-step">
            <span className="apidocs-step-num">2</span>
            <span>When an event occurs (email sent, reply received, bounce, etc.), SynxReach automatically POSTs the event data to your URL</span>
          </div>
          <div className="apidocs-quickstart-step">
            <span className="apidocs-step-num">3</span>
            <span>Verify the signature using the webhook secret to ensure authenticity</span>
          </div>
        </div>

        <p style={{ margin: '1rem 0 0.5rem', fontWeight: 600 }}>Supported Events</p>
        <table className="apidocs-auth-table" style={{ marginBottom: '0.75rem' }}>
          <thead><tr><th>Event</th><th>Triggered When</th></tr></thead>
          <tbody>
            <tr><td><code>send</code></td><td>An email is successfully sent to a contact</td></tr>
            <tr><td><code>open</code></td><td>A recipient opens an email (tracking pixel loaded)</td></tr>
            <tr><td><code>click</code></td><td>A recipient clicks a tracked link in the email</td></tr>
            <tr><td><code>reply</code></td><td>A recipient replies to a campaign email</td></tr>
            <tr><td><code>bounce</code></td><td>An email bounces and the contact is marked as bounced</td></tr>
            <tr><td><code>unsubscribe</code></td><td>A contact is marked as unsubscribed (auto-detected from reply)</td></tr>
            <tr><td><code>status_change</code></td><td>A campaign status changes (started, paused, resumed, completed, auto-paused)</td></tr>
          </tbody>
        </table>

        <p style={{ margin: '0.5rem 0', fontWeight: 600 }}>Payload Format</p>
        <div className="apidocs-code-wrapper">
          <pre><code>{`// POST to your webhook URL
// Headers:
//   X-SynxReach-Signature: t=1710000000,v1=abc123...
//   X-SynxReach-Event: reply
//   X-SynxReach-Delivery-Id: del_a1b2c3d4...
//   Content-Type: application/json

{
  "id": "evt_f8a2c9d1e4b7...",
  "event": "reply",
  "created_at": "2026-03-16T14:30:00+00:00",
  "data": {
    "campaign_id": 42,
    "contact_id": 100,
    "email": "lead@example.com",
    "subject": "Re: Spring Campaign",
    "snippet": "Thanks for reaching out..."
  }
}`}</code></pre>
        </div>

        <p style={{ margin: '1rem 0 0.5rem', fontWeight: 600 }}>Signature Verification</p>
        <div className="apidocs-code-wrapper">
          <pre><code>{`import hmac, hashlib, json

def verify_signature(payload, signature_header, secret):
    parts = dict(p.split("=", 1) for p in signature_header.split(","))
    timestamp = parts["t"]
    body = json.dumps(payload, separators=(",", ":"), sort_keys=True)
    expected = hmac.new(
        secret.encode(), f"{timestamp}.{body}".encode(), hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, parts["v1"])`}</code></pre>
        </div>
      </div>

      {/* Search */}
      <div className="apidocs-search-bar">
        <Search size={18} className="apidocs-search-icon" />
        <input
          type="text"
          placeholder="Search endpoints..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <span className="apidocs-search-count">
            {filteredSections.reduce((sum, s) => sum + s.endpoints.length, 0)} results
          </span>
        )}
      </div>

      {/* Section nav */}
      <div className="apidocs-section-nav">
        {API_SECTIONS.map(section => (
          <a
            key={section.id}
            href={`#section-${section.id}`}
            className={`apidocs-section-chip ${activeSection === section.id ? 'active' : ''}`}
            onClick={e => {
              e.preventDefault()
              setActiveSection(section.id === activeSection ? null : section.id)
              document.getElementById(`section-${section.id}`)?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            <section.icon size={14} />
            {section.title}
            <span className="apidocs-chip-count">{section.endpoints.length}</span>
          </a>
        ))}
      </div>

      {/* Endpoint sections */}
      {filteredSections.map(section => (
        <div key={section.id} id={`section-${section.id}`} className="apidocs-section">
          <div className="apidocs-section-header">
            <section.icon size={22} className="apidocs-section-icon" />
            <div>
              <h3>{section.title}</h3>
              <p>{section.description}</p>
            </div>
            <span className="apidocs-scope-badge">scope: {section.scope}</span>
            <span className="apidocs-endpoint-count">{section.endpoints.length} endpoints</span>
          </div>
          <div className="apidocs-endpoints">
            {section.endpoints.map((ep, i) => (
              <EndpointCard key={i} endpoint={ep} />
            ))}
          </div>
        </div>
      ))}

      {filteredSections.length === 0 && (
        <div className="apidocs-empty">
          <Search size={48} />
          <h3>No endpoints found</h3>
          <p>Try a different search term</p>
        </div>
      )}
    </div>
  )
}

export default ApiDocs
